/**
 * <pg-product-recommendations> and <pg-recently-viewed>: product rows that load after the page,
 * rendered on the server (Section Rendering API) and swapped in when they near the viewport.
 *
 * - Recommendations: Shopify's product recommendations endpoint (related / complementary).
 * - Recently viewed: handles kept in localStorage by sections/pg-main-product.liquid. The section
 *   is rendered at /search?q=<handles>, where it reads them from `search.terms` and looks each
 *   product up with all_products, so cards are real pg-product-card markup in viewing order.
 *
 * Both hide themselves when there's nothing to show, and announce new markup with
 * `pg:content:rendered` (partner prices, reveals).
 */
import { PgElement, define } from '@pg/core';
import { adoptSectionStyles, fetchSection } from '@pg/section-renderer';

/** Run `callback` once the element is within ~600px of the viewport. */
function whenNear(element, callback) {
  if (!('IntersectionObserver' in window)) return callback();
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      callback();
    },
    { rootMargin: '600px 0px' },
  );
  observer.observe(element);
}

/** @param {Element} target @param {string} html */
function swap(target, html) {
  const next = new DOMParser().parseFromString(html, 'text/html').querySelector(target.localName);
  const content = next?.querySelector('[data-pg-recommendations-content]');
  if (!next || !content || !content.querySelector('pg-product-card')) {
    target.remove();
    return;
  }
  target.querySelector('[data-pg-recommendations-content]')?.replaceWith(content);
  target.removeAttribute('hidden');
  target.dispatchEvent(new CustomEvent('pg:content:rendered', { bubbles: true }));
}

class PgProductRecommendations extends PgElement {
  mount() {
    if (this.querySelector('pg-product-card') || this.hasAttribute('hidden')) return;
    whenNear(this, async () => {
      try {
        const response = await fetch(this.dataset.url || '');
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        adoptSectionStyles(html);
        swap(this, html);
      } catch {
        this.remove();
      }
    });
  }
}

const STORAGE_KEY = 'pg-recently-viewed';

class PgRecentlyViewed extends PgElement {
  mount() {
    if (this.querySelector('pg-product-card')) return;
    /** @type {string[]} */
    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      handles = [];
    }
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    handles = handles.filter((h) => typeof h === 'string' && /^[\w-]+$/.test(h) && !exclude.has(h)).slice(0, 8);
    if (!handles.length) return;

    whenNear(this, async () => {
      const url = new URL(this.dataset.searchUrl || '/search', location.origin);
      url.searchParams.set('q', handles.join(','));
      url.searchParams.set('type', 'product');
      try {
        swap(this, await fetchSection(this.dataset.sectionId || 'pg-recently-viewed', url.toString(), { cache: false }));
      } catch {
        /* stays hidden */
      }
    });
  }
}

define('pg-product-recommendations', PgProductRecommendations);
define('pg-recently-viewed', PgRecentlyViewed);
